import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getTenderProcessList, approveTenderProcess } from "../../../services/AppService";
import { useAuth } from "../../../context/AuthContext";
import closeButton from "../../../assets/images/close.png";
import MessageModal from "../../../components/AdminComponents/Modals/MessageModal";
import ViewApprovalTenderDetailsModal from "../../../components/dashboard/Approvals/TenderProcessComponents/ViewApprovalTenderDetailsModal";
import ApproveActionModal from "../../../components/dashboard/Approvals/ApprovalsPlanEstimate/ApproveActionModal";

const ApproveTenderProcess = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [tenders, setTenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [search, setSearch] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);


    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [selectedTender, setSelectedTender] = useState(null);
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const isOfficer = user?.designation?.toLowerCase().includes("officer");

    useEffect(() => {
        fetchTenders();
    }, [page, size, search]);

    const fetchTenders = async () => {
        try {
            setLoading(true);
            const response = await getTenderProcessList(search, page, size);
            // Ensuring we map the content from the data object
            setTenders(response.content || []);
            setTotalPages(response.totalPages || 0);
            setTotalElements(response.totalElements || 0);
        } catch (error) {
            console.error("Failed to fetch tenders", error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmApproval = async (id, remark) => {
        try {
            await approveTenderProcess(id, remark);
            setApproveModalOpen(false);
            await fetchTenders();
            setMessageModal({ open: true, type: "success", title: "Success", message: "Tender Process Approved Successfully" });
        } catch (error) {
            setMessageModal({ open: true, type: "error", title: "Error", message: error.response?.data?.message || "Approval Failed" });
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t('approvals.headingApproveTenderProcess')}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2 text-sm">
                    <label>{t('common.show')}</label>
                    <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2">
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                    <label>{t('common.entries')}</label>
                </div>
                <input type="text" placeholder={t('common.search')} className="border rounded px-3 py-1 text-sm" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
            </div>


            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 border-b border-black">{t('tables.srNo')}</th>
                            <th className="px-4 py-2 border-b border-black">{t('forms.planEstimate.workName')}</th>
                            <th className="px-4 py-2 border-b border-black">{t('tables.tenderProcess.tenderReferenceNo')}</th>
                            <th className="px-4 py-2 border-b border-black">{t('tables.tenderProcess.tenderId')}</th>
                            <th className="px-3 py-2 border-b border-black">{t('tables.view')}</th>
                            <th className="px-4 py-2 border-b border-black">{t('tables.approve')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-4">Loading...</td></tr>
                        ) : tenders.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-4">No records found.</td></tr>
                        ) : (
                            tenders.map((item, idx) => (
                                <tr key={item.id} className="hover:bg-gray-50 border-b">
                                    <td className="p-2 text-center">{page * size + idx + 1}</td>
                                    {/* Using workTypeName as work name from response */}
                                    <td className="p-2">{item.workTypeName}</td>
                                    {/* Corrected Key: tenderReferenceNo */}
                                    <td className="p-2">{item.tenderReferenceNo}</td>
                                    <td className="p-2">{item.tenderId}</td>
                                    <td className="p-2 text-center">
                                        <button className="btnImgView p-1" onClick={() => { setSelectedTender(item); setViewModalOpen(true); }}></button>
                                    </td>
                                    <td className="p-2 text-center">
                                        {item.isApproved ? (
                                            <span className="text-green-600 font-bold uppercase text-xs">Approved</span>
                                        ) : (
                                            isOfficer && item.createdBy !== user?.userId && (
                                                <button className="staff-btn-save"
                                                    onClick={() => { setSelectedTender(item); setApproveModalOpen(true); }}>
                                                    Approve
                                                </button>
                                            )
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4 border-t pt-4">
                <div className="text-sm text-gray-600">
                    {t('common.showing')} {tenders.length > 0 ? page * size + 1 : 0} {t('common.to')} {Math.min((page + 1) * size, totalElements)} {t('common.of')} {totalElements} {t('common.entries')}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className={`px-3 py-1 border rounded text-sm ${page === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-gray-50'}`}
                        onClick={() => setPage(prev => Math.max(0, prev - 1))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            className={`px-3 py-1 border rounded text-sm ${page === i ? 'bg-[#a0adf1] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                            onClick={() => setPage(i)}
                        >
                            {i + 1}
                        </button>
                    )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}

                    <button
                        className={`px-3 py-1 border rounded text-sm ${page >= totalPages - 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-gray-50'}`}
                        onClick={() => setPage(prev => Math.min(totalPages - 1, prev + 1))}
                        disabled={page >= totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>


            <ViewApprovalTenderDetailsModal isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)} tender={selectedTender} title={t('approvals.approveTenderProcess')}/>
            <ApproveActionModal isOpen={approveModalOpen} title={t('approvals.doApproveTenderProcess')} onClose={() => setApproveModalOpen(false)} onConfirm={handleConfirmApproval} estimate={selectedTender} />
            <MessageModal open={messageModal.open} type={messageModal.type} title={messageModal.title} message={messageModal.message} onClose={() => setMessageModal({ ...messageModal, open: false })} />
        </div>
    );
};

export default ApproveTenderProcess;